import type { Seed } from '@/types';

/**
 * A row is a preseed iff its key carries the backend's `#preseed#` prefix.
 *
 * Keys are minted at exactly one site — `backend/pkg/tabularium/model/preseed.go:191-193`,
 * `#preseed#<type>#<title>#<value>` — and the prefix survives every frontend
 * transform, unlike `type` (wire shape only) or `class` (adapter shape only).
 * ENG-5427 retired the duck-type this replaces — a test for a `type` field
 * being present while a `class` field was absent. It read the row *shape*, so
 * it answered differently for the two shapes a preseed row can arrive in.
 *
 * Deliberately NOT a `seed is Preseed` type predicate: the global `Seed` and
 * `Preseed` types share no discriminant, so narrowing on this test would be
 * unsound. Callers get a boolean and keep their own casts.
 *
 * Deliberately NOT expressed via `Regex.PRESEED_KEY` (`@/utils/regex.util`), and
 * deliberately broader than it: that regex requires three *non-empty* segments,
 * so it rejects a malformed key like `#preseed#whois+company##` that this test
 * accepts. Being broader is the fail-safe direction at an action boundary — a
 * malformed preseed key stays on the preseed path instead of being mutated as a
 * regular seed. Classify with this helper; parse with the regex. Do not
 * delegate one to the other.
 *
 * Fail-safe: a missing row is not a preseed.
 */
export function isPreseedKey(
  seed: Pick<Seed, 'key'> | null | undefined
): boolean {
  return seed?.key.startsWith('#preseed#') ?? false;
}
